import React from 'react';
import { Text } from '@mantine/core';
import { parseIframeDimensionForPreview } from './productEmbedUtils';

/**
 * Live-ish embed preview: iframe uses the same logical size as the generated code, scaled to fit
 * inside a monitor-style frame so presets feel tangible without loading a full-size iframe.
 */
export function EmbedIframeMonitorPreview({
  iframeProps,
  maxMonitorWidth = 400,
  maxScreenHeight = 220,
  compact = false,
  approxSizeLabel,
}: {
  iframeProps: React.IframeHTMLAttributes<HTMLIFrameElement>;
  maxMonitorWidth?: number;
  maxScreenHeight?: number;
  compact?: boolean;
  approxSizeLabel: string;
}) {
  const rawW = iframeProps.width;
  const rawH = iframeProps.height;
  const fallbackW = 560;
  const fallbackH = 360;
  const innerW = parseIframeDimensionForPreview(
    typeof rawW === 'number' ? `${rawW}px` : rawW,
    fallbackW,
    'w'
  );
  const innerH = parseIframeDimensionForPreview(
    typeof rawH === 'number' ? `${rawH}px` : rawH,
    fallbackH,
    'h'
  );

  const bezel = compact ? 8 : 10;
  const screenPad = compact ? 6 : 8;
  const topBarH = compact ? 14 : 18;
  const maxScreenW = maxMonitorWidth - 2 * (bezel + screenPad);

  const scale = Math.min(maxScreenW / innerW, maxScreenHeight / innerH, 1);
  const scaledW = innerW * scale;
  const scaledH = innerH * scale;

  const { width: _omitW, height: _omitH, style: iframeStyle, ...restIframe } = iframeProps;

  return (
    <div
      style={{
        width: '100%',
        maxWidth: `min(100%, ${maxMonitorWidth}px)`,
        margin: '0 auto',
        minWidth: 0,
      }}
    >
      <div
        style={{
          borderRadius: compact ? 10 : 14,
          padding: bezel,
          background: 'linear-gradient(160deg, #2d2d32 0%, #1a1a1e 40%, #0f0f12 100%)',
          boxShadow: '0 12px 28px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
          <div
            style={{
              width: compact ? 36 : 48,
              height: compact ? 4 : 5,
              borderRadius: 4,
              background: '#0a0a0c',
              boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.15)',
            }}
          />
        </div>
        <div
          style={{
            borderRadius: compact ? 6 : 8,
            padding: screenPad,
            background: '#0c0c0e',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04)',
          }}
        >
          <div
            style={{
              height: topBarH,
              borderRadius: '4px 4px 0 0',
              background:
                'linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
              marginBottom: 4,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 8,
              gap: 4,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ff5f57' }} />
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#febc2e' }} />
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#28c840' }} />
          </div>
          <div
            style={{
              width: scaledW,
              height: scaledH,
              overflow: 'hidden',
              margin: '0 auto',
              borderRadius: 4,
              background: '#111',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
            }}
          >
            <div
              style={{
                width: innerW,
                height: innerH,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <iframe
                {...restIframe}
                width={innerW}
                height={innerH}
                style={{
                  border: 'none',
                  display: 'block',
                  ...(typeof iframeStyle === 'object' && iframeStyle !== null ? iframeStyle : {}),
                }}
              />
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: compact ? 6 : 8,
          }}
        >
          <div
            style={{
              width: compact ? '28%' : '32%',
              height: compact ? 10 : 12,
              background: 'linear-gradient(180deg, #252528 0%, #151517 100%)',
              borderRadius: '0 0 4px 4px',
            }}
          />
          <div
            style={{
              width: compact ? '42%' : '48%',
              height: compact ? 4 : 5,
              marginTop: 2,
              background: '#1c1c1f',
              borderRadius: 2,
            }}
          />
        </div>
      </div>
      <Text size="xs" c="dimmed" ta="center" mt={4} style={{ lineHeight: 1.35 }}>
        {approxSizeLabel}
      </Text>
    </div>
  );
}
